import React, { useState, useEffect, useRef, useMemo } from "react";
import apiClient from "@/utils/apiClient";
import downloadFile from "@/utils/fileDownloader";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import {
  FolderPlus,
  UploadCloud,
  X,
  Trash2,
  Tag,
  Filter,
  Share2,
  ChevronLeft,
  ChevronRight,
  Maximize2,
  Calendar,
  Layers,
  FileText,
  AlertCircle,
  Video,
  ExternalLink,
  Plus,
  Search,
  Check,
  Facebook,
  Twitter,
  Copy,
  CheckCircle,
  HelpCircle,
  Download,
  Edit
} from "lucide-react";

const GalleryManager = ({ role = "admin", permissions }) => {
  const { user } = useAuth();
  const apiPrefix = role === "admin" ? "/v1/admin/gallery" : "/v1/warden/gallery";
  
  // State variables
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [media, setMedia] = useState([]);
  const [students, setStudents] = useState([]);
  const [searchStudentQuery, setSearchStudentQuery] = useState("");
  
  // Tag on upload state
  const isForcedIndividual = role === "warden" && !permissions?.can_manage_gallery;
  const [tagOnUpload, setTagOnUpload] = useState(isForcedIndividual);
  const [uploadSelectedClass, setUploadSelectedClass] = useState("All");
  const [uploadSelectedRoom, setUploadSelectedRoom] = useState("All");
  const [uploadSearchQuery, setUploadSearchQuery] = useState("");
  const [uploadSelectedStudents, setUploadSelectedStudents] = useState([]);
  const [tagModalSelectedClass, setTagModalSelectedClass] = useState("All");
  const [tagModalSelectedRoom, setTagModalSelectedRoom] = useState("All");
  
  // Permissions control
  const canManage = role === "admin" || (role === "warden" && !!permissions?.can_manage_gallery && students.length > 0);

  // Memoized unique classes from students
  const uniqueClasses = useMemo(() => {
    const classes = new Set(students.map(s => s.class_name).filter(Boolean));
    return Array.from(classes).sort();
  }, [students]);

  // Memoized unique rooms from students
  const uniqueRooms = useMemo(() => {
    const rooms = new Set(students.map(s => s.room_number).filter(Boolean));
    return Array.from(rooms).sort();
  }, [students]);


  
  // Loading states
  const [loadingEvents, setLoadingEvents] = useState(true);
  const [loadingMedia, setLoadingMedia] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  
  // Modals / Dropdowns
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showTagModal, setShowTagModal] = useState(false);
  const [taggingMediaId, setTaggingMediaId] = useState(null);
  const [taggedStudentsTemp, setTaggedStudentsTemp] = useState([]);
  
  // Lightbox
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  
  // Share tray
  const [showShareIndex, setShowShareIndex] = useState(null);
  const [copiedIndex, setCopiedIndex] = useState(null);

  // New Event Form State
  const [newEvent, setNewEvent] = useState({
    title: "",
    description: "",
    date: new Date().toISOString().split("T")[0],
    tags: "",
  });

  // Edit Event Form State
  const [showEditModal, setShowEditModal] = useState(false);
  const [editEventData, setEditEventData] = useState({
    id: "",
    title: "",
    description: "",
    date: "",
    tags: "",
  });

  // Upload States
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [autoCompress, setAutoCompress] = useState(true);
  const fileInputRef = useRef(null);
  const photosInputRef = useRef(null);
  const videosInputRef = useRef(null);

  // Load events and student database for tagging
  useEffect(() => {
    fetchEvents();
    fetchStudents();
  }, []);

  // Refresh media when event is chosen
  useEffect(() => {
    if (selectedEvent) {
      fetchEventMedia(selectedEvent.id);
    } else {
      setMedia([]);
    }
  }, [selectedEvent]);

  useEffect(() => {
    setTagOnUpload(role === "warden" && !permissions?.can_manage_gallery);
  }, [permissions, role]);

  // API Call: Fetch all events
  const fetchEvents = async () => {
    setLoadingEvents(true);
    try {
      const response = await apiClient.get(`${apiPrefix}/events`);
      setEvents(response.data.events || []);
    } catch (error) {
      console.error("Error fetching events:", error);
    } finally {
      setLoadingEvents(false);
    }
  };

  // API Call: Fetch students
  const fetchStudents = async () => {
    try {
      if (role === "admin") {
        if (user?.hostel_id) {
          const response = await apiClient.get(`/v1/admin/students/?hostel_id=${user.hostel_id}`);
          setStudents(Array.isArray(response.data) ? response.data : []);
        }
      } else {
        const response = await apiClient.get("/v1/warden/students/my-students");
        setStudents(response.data.students || []);
      }
    } catch (error) {
      console.error("Error fetching students:", error);
    }
  };

  // API Call: Fetch media inside event
  const fetchEventMedia = async (eventId) => {
    setLoadingMedia(true);
    try {
      const response = await apiClient.get(`${apiPrefix}/events/${eventId}`);
      setMedia(response.data.media || []);
    } catch (error) {
      console.error("Error fetching event media:", error);
    } finally {
      setLoadingMedia(false);
    }
  };

  // Create event submission
  const handleCreateEvent = async (e) => {
    e.preventDefault();
    if (!newEvent.title.trim()) return;

    try {
      const tagsArray = newEvent.tags
        ? newEvent.tags.split(",").map((t) => t.trim()).filter((t) => t)
        : [];
      
      const payload = {
        title: newEvent.title,
        description: newEvent.description,
        date: newEvent.date,
        tags: tagsArray,
      };

      const response = await apiClient.post(`${apiPrefix}/events`, payload);
      setEvents((prev) => [response.data, ...prev]);
      setSelectedEvent(response.data);
      setShowCreateModal(false);
      setNewEvent({
        title: "",
        description: "",
        date: new Date().toISOString().split("T")[0],
        tags: "",
      });
    } catch (error) {
      console.error("Error creating event:", error);
      alert("Failed to create gallery album. Check console for details.");
    }
  };

  // Open Edit Event Modal
  const openEditModal = (eventObj) => {
    setEditEventData({
      id: eventObj.id,
      title: eventObj.title,
      description: eventObj.description || "",
      date: eventObj.date || "",
      tags: eventObj.tags ? eventObj.tags.join(", ") : "",
    });
    setShowEditModal(true);
  };

  // Edit Event Submission
  const handleUpdateEvent = async (e) => {
    e.preventDefault();
    if (!editEventData.title.trim()) return;

    try {
      const tagsArray = editEventData.tags
        ? editEventData.tags.split(",").map((t) => t.trim()).filter((t) => t)
        : [];
      
      const payload = {
        title: editEventData.title,
        description: editEventData.description,
        date: editEventData.date,
        tags: tagsArray,
      };

      const response = await apiClient.put(`${apiPrefix}/events/${editEventData.id}`, payload);
      
      // Update local lists
      setEvents((prev) =>
        prev.map((evt) => (evt.id === editEventData.id ? { ...evt, ...response.data } : evt))
      );
      
      if (selectedEvent?.id === editEventData.id) {
        setSelectedEvent((prev) => ({ ...prev, ...response.data }));
      }
      
      setShowEditModal(false);
    } catch (error) {
      console.error("Error updating event:", error);
      alert("Failed to update gallery album.");
    }
  };

  // Delete event
  const handleDeleteEvent = async (eventId) => {
    if (!confirm("Are you sure you want to delete this album? ALL photos and videos inside will be permanently deleted from both the database and Google Drive!")) {
      return;
    }

    try {
      await apiClient.delete(`${apiPrefix}/events/${eventId}`);
      setEvents((prev) => prev.filter((e) => e.id !== eventId));
      if (selectedEvent?.id === eventId) {
        setSelectedEvent(null);
      }
    } catch (error) {
      console.error("Error deleting event:", error);
      alert("Failed to delete event.");
    }
  };

  const validateFiles = (filesArr) => {
    const validFiles = [];
    const errors = [];

    filesArr.forEach((file) => {
      const extension = file.name.split(".").pop().toLowerCase();
      const isImage = file.type.startsWith("image/") || ["webp", "jpeg", "jpg"].includes(extension);
      const isVideo = file.type.startsWith("video/") || ["mp4"].includes(extension);

      if (isImage) {
        if (extension !== "webp" && extension !== "jpeg" && extension !== "jpg") {
          errors.push(`${file.name}: Only .webp, .jpeg/jpg formats are allowed for images.`);
          return;
        }
        if (file.size > 10 * 1024 * 1024) {
          errors.push(`${file.name}: Image size exceeds 10MB limit.`);
          return;
        }
      } else if (isVideo) {
        if (extension !== "mp4") {
          errors.push(`${file.name}: Only .mp4 format is allowed for videos.`);
          return;
        }
        if (file.size > 20 * 1024 * 1024) {
          errors.push(`${file.name}: Video size exceeds 20MB limit.`);
          return;
        }
      } else {
        errors.push(`${file.name}: Unsupported file type.`);
        return;
      }

      validFiles.push(file);
    });

    if (errors.length > 0) {
      alert(errors.join("\n"));
    }

    return validFiles;
  };

  // Drag and Drop handlers
  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    if (e.dataTransfer.files) {
      const incoming = Array.from(e.dataTransfer.files);
      const validated = validateFiles(incoming);
      
      setSelectedFiles((prev) => {
        const total = prev.length + validated.length;
        if (total > 10) {
          alert("Maximum 10 files can be uploaded at a time.");
          const allowedCount = 10 - prev.length;
          return [...prev, ...validated.slice(0, allowedCount)];
        }
        return [...prev, ...validated];
      });
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files) {
      const incoming = Array.from(e.target.files);
      const validated = validateFiles(incoming);
      
      setSelectedFiles((prev) => {
        const total = prev.length + validated.length;
        if (total > 10) {
          alert("Maximum 10 files can be uploaded at a time.");
          const allowedCount = 10 - prev.length;
          return [...prev, ...validated.slice(0, allowedCount)];
        }
        return [...prev, ...validated];
      });
    }
  };

  const removeFileFromQueue = (index) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  // Upload Media Files Queue
  const handleUploadFiles = async () => {
    if (selectedFiles.length === 0) return;

    const isIndividual = tagOnUpload || isForcedIndividual;
    if (isIndividual && uploadSelectedStudents.length === 0) {
      alert("Please select at least one student to tag for individual upload.");
      return;
    }

    const eventId = selectedEvent ? selectedEvent.id : "default";
    setUploading(true);
    setUploadProgress(0);

    const totalFiles = selectedFiles.length;
    let successfulUploads = 0;

    for (let i = 0; i < totalFiles; i++) {
      const file = selectedFiles[i];
      const formData = new FormData();
      formData.append("file", file);
      formData.append("compress", autoCompress ? "true" : "false");
      formData.append("tagged_students_json", JSON.stringify(isIndividual ? uploadSelectedStudents : []));

      try {
        await apiClient.post(`${apiPrefix}/events/${eventId}/media`, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
        successfulUploads++;
        setUploadProgress(Math.round((successfulUploads / totalFiles) * 100));
      } catch (error) {
        console.error(`Error uploading file ${file.name}:`, error);
      }
    }

    setSelectedFiles([]);
    setUploading(false);
    setUploadSelectedStudents([]);
    setUploadSelectedClass("All");
    setUploadSelectedRoom("All");
    setUploadSearchQuery("");
    setTagOnUpload(isForcedIndividual);
    if (selectedEvent) {
      fetchEventMedia(selectedEvent.id);
    } else {
      fetchEvents();
    }
  };

  // Delete Media Item
  const handleDeleteMedia = async (mediaId) => {
    if (!confirm("Are you sure you want to delete this media file from Google Drive?")) {
      return;
    }

    try {
      await apiClient.delete(`${apiPrefix}/media/${mediaId}`);
      setMedia((prev) => prev.filter((m) => m.id !== mediaId));
    } catch (error) {
      console.error("Error deleting media:", error);
      alert("Failed to delete media file.");
    }
  };

  // Download Media File via Universal Downloader
  const handleDownload = async (mediaItem) => {
    try {
      const response = await apiClient.get(`${apiPrefix}/media/${mediaItem.id}/download`, {
        responseType: "blob"
      });
      const mimeType = response.headers?.["content-type"] || "image/jpeg";
      await downloadFile(response.data, mediaItem.original_name, mimeType);
    } catch (error) {
      console.error("Error downloading file:", error);
      alert("Failed to download file.");
    }
  };

  // Manage Tags Modal opening
  const openTagModal = (mediaItem) => {
    setTaggingMediaId(mediaItem.id);
    setTaggedStudentsTemp(mediaItem.tagged_students || []);
    setSearchStudentQuery("");
    setTagModalSelectedClass("All");
    setTagModalSelectedRoom("All");
    setShowTagModal(true);
  };

  // Toggle student tag in list
  const toggleStudentTag = (studentId) => {
    setTaggedStudentsTemp((prev) =>
      prev.includes(studentId)
        ? prev.filter((id) => id !== studentId)
        : [...prev, studentId]
    );
  };

  // Save student tags
  const handleSaveTags = async () => {
    try {
      const response = await apiClient.put(`${apiPrefix}/media/${taggingMediaId}/tags`, {
        tagged_students: taggedStudentsTemp,
      });
      
      // Update local state
      setMedia((prev) =>
        prev.map((m) =>
          m.id === taggingMediaId
            ? { ...m, tagged_students: response.data.media.tagged_students }
            : m
        )
      );
      setShowTagModal(false);
    } catch (error) {
      console.error("Error updating tags:", error);
      alert("Failed to update tags.");
    }
  };

  // Fullscreen view lightbox controls
  const openLightbox = (index) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const prevLightboxItem = () => {
    setLightboxIndex((prev) => (prev === 0 ? media.length - 1 : prev - 1));
  };

  const nextLightboxItem = () => {
    setLightboxIndex((prev) => (prev === media.length - 1 ? 0 : prev + 1));
  };

  // Share Tray Action
  const copyShareLink = (url, index) => {
    navigator.clipboard.writeText(url);
    setCopiedIndex(index);
    setTimeout(() => setCopiedIndex(null), 2000);
  };

  const shareSocial = (platform, url, title = "School Event Gallery") => {
    const text = encodeURIComponent(`Check out the school gallery event: ${title}!`);
    const link = encodeURIComponent(url);
    let shareUrl = "";

    if (platform === "facebook") {
      shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${link}`;
    } else if (platform === "twitter") {
      shareUrl = `https://twitter.com/intent/tweet?url=${link}&text=${text}`;
    } else if (platform === "whatsapp") {
      shareUrl = `https://api.whatsapp.com/send?text=${text}%20${link}`;
    }

    if (shareUrl) {
      window.open(shareUrl, "_blank", "width=600,height=400");
    }
  };

  // Filters students database list
  const filteredStudents = students.filter((s) => {
    const matchesSearch =
      !searchStudentQuery ||
      s.name.toLowerCase().includes(searchStudentQuery.toLowerCase()) ||
      (s.roll_no && s.roll_no.toString().includes(searchStudentQuery)) ||
      (s.class_name && s.class_name.toLowerCase().includes(searchStudentQuery.toLowerCase()));
    
    const matchesClass = tagModalSelectedClass === "All" || s.class_name === tagModalSelectedClass;
    const matchesRoom = tagModalSelectedRoom === "All" || s.room_number === tagModalSelectedRoom;

    return matchesSearch && matchesClass && matchesRoom;
  });

  return (
    <div className="space-y-6">
      {/* Upper header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 bg-white p-5 rounded-2xl border border-slate-100 shadow-sm">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <Layers className="w-6 h-6 text-sky-500" />
            School Media Gallery
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Create albums, upload photos & videos, compress file sizes, and tag students to share with parents.
          </p>
        </div>
        <div className="flex gap-2">
          {selectedEvent && (
            <Button
              variant="outline"
              onClick={() => setSelectedEvent(null)}
              className="border-slate-200 hover:bg-slate-50 text-slate-700"
            >
              Back to Albums
            </Button>
          )}
          {canManage && (
            <Button
              onClick={() => setShowCreateModal(true)}
              className="bg-gradient-to-r from-sky-500 to-indigo-500 text-white hover:from-sky-600 hover:to-indigo-600 shadow-md"
            >
              <FolderPlus className="w-4 h-4 mr-2" />
              Create Album
            </Button>
          )}
        </div>
      </div>

      {/* Main Container */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left/Main Column: Album List OR Media Grid */}
        <div className={canManage ? "lg:col-span-2 space-y-6" : "lg:col-span-3 space-y-6"}>
          {!selectedEvent ? (
            // Event List View
            loadingEvents ? (
              <div className="flex items-center justify-center h-64 bg-white rounded-2xl border border-slate-100 shadow-sm">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-sky-500"></div>
              </div>
            ) : events.length === 0 ? (
              <div className="bg-white border border-dashed border-slate-200 rounded-2xl p-16 text-center shadow-sm">
                <FolderPlus className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-700">No Albums Created Yet</h3>
                <p className="text-slate-500 max-w-sm mx-auto mt-2 text-sm">
                  Get started by creating your first school event gallery album. Wardens and parents can view files inside!
                </p>
                {canManage && (
                  <Button
                    onClick={() => setShowCreateModal(true)}
                    className="bg-sky-500 text-white mt-6 hover:bg-sky-600"
                  >
                    Create Album Now
                  </Button>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {events.map((event) => (
                  <div
                    key={event.id}
                    onClick={() => setSelectedEvent(event)}
                    className="bg-white rounded-2xl overflow-hidden border border-slate-100 shadow-sm hover:shadow-lg transition-all duration-300 group flex flex-col justify-between cursor-pointer hover:border-sky-200 hover:-translate-y-0.5"
                  >
                    <div className="relative aspect-video bg-slate-100 flex items-center justify-center overflow-hidden">
                      {event.cover_image_url ? (
                        <img
                          src={event.cover_image_url}
                          alt={event.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          loading="lazy"
                        />
                      ) : (
                        <div className="flex flex-col items-center justify-center text-slate-400">
                          <Layers className="w-10 h-10 stroke-1 mb-2" />
                          <span className="text-xs">Empty Album</span>
                        </div>
                      )}
                      <div className="absolute top-3 left-3 bg-black/40 backdrop-blur-md text-white text-xs px-2.5 py-1 rounded-full flex items-center gap-1 font-medium">
                        <Calendar className="w-3.5 h-3.5" />
                        {event.date}
                      </div>
                    </div>

                    <div className="p-5 flex-1 flex flex-col justify-between">
                      <div>
                        <h3 className="font-bold text-slate-800 text-lg group-hover:text-sky-500 transition-colors line-clamp-1">
                          {event.title}
                        </h3>
                        <p className="text-slate-500 text-sm mt-2 line-clamp-2">
                          {event.description || "No description provided."}
                        </p>
                        {event.tags && event.tags.length > 0 && (
                          <div className="flex flex-wrap gap-1.5 mt-4">
                            {event.tags.map((tag) => (
                              <span
                                key={tag}
                                className="bg-sky-50 text-sky-600 text-xs px-2 py-0.5 rounded font-medium"
                              >
                                {tag}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>

                      <div className="flex items-center justify-between border-t border-slate-50 mt-6 pt-4">
                        <span className="text-sky-500 group-hover:text-sky-600 font-semibold text-sm flex items-center gap-1.5 transition-colors">
                          View Album Moments &rarr;
                        </span>
                        {canManage && (
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={(e) => {
                                e.stopPropagation();
                                openEditModal(event);
                              }}
                              className="text-slate-400 hover:text-slate-600 hover:bg-slate-50"
                              title="Edit Album Details"
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteEvent(event.id);
                              }}
                              className="text-red-400 hover:text-red-600 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )
          ) : (
            // Event Gallery Details View
            <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm space-y-6">
              <div className="border-b border-slate-100 pb-4 mb-4 flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <h2 className="text-xl font-bold text-slate-800">{selectedEvent.title}</h2>
                    <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full font-medium">
                      {selectedEvent.date}
                    </span>
                    {canManage && (
                      <button
                        onClick={() => openEditModal(selectedEvent)}
                        className="p-1 text-slate-400 hover:text-sky-500 rounded transition-colors"
                        title="Edit Album Details"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  <p className="text-slate-500 text-sm mt-1">{selectedEvent.description}</p>
                </div>
                <Button
                  variant="outline"
                  onClick={() => setSelectedEvent(null)}
                  className="text-xs border-slate-200"
                >
                  Close Album
                </Button>
              </div>

              {/* Media Items Grid */}
              {loadingMedia ? (
                <div className="flex items-center justify-center h-64">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-sky-500"></div>
                </div>
              ) : media.length === 0 ? (
                <div className="text-center py-16 text-slate-400">
                  <UploadCloud className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                  <p className="text-sm">This album has no media files. Upload photos and videos to get started.</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {media.map((item, index) => (
                    <div
                      key={item.id}
                      onClick={() => openLightbox(index)}
                      className="relative rounded-xl overflow-hidden aspect-square bg-slate-900 group border border-slate-100 cursor-pointer shadow-sm hover:scale-[1.02] hover:shadow-md transition-all duration-200"
                    >
                      {item.media_type === "video" ? (
                        <div className="w-full h-full relative flex items-center justify-center">
                          <img
                            src={item.thumbnail_link}
                            alt={item.original_name}
                            className="w-full h-full object-cover opacity-80"
                            loading="lazy"
                          />
                          <div className="absolute inset-0 bg-black/35 flex items-center justify-center">
                            <div className="w-10 h-10 bg-white/20 backdrop-blur-md rounded-full flex items-center justify-center text-white">
                              <Video className="w-5 h-5 fill-white" />
                            </div>
                          </div>
                        </div>
                      ) : (
                        <img
                          src={item.thumbnail_link}
                          alt={item.original_name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          loading="lazy"
                        />
                      )}

                      {/* Overlays / Hover Buttons */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/30 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-between p-3 text-white">
                        <div className="flex justify-between items-start">
                          <span className="text-[10px] bg-sky-500 text-white font-medium px-2 py-0.5 rounded-full truncate max-w-[100px]">
                            {item.tagged_students ? `${item.tagged_students.length} Tagged` : "0 Tagged"}
                          </span>
                          {canManage && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteMedia(item.id);
                              }}
                              className="p-1.5 bg-red-500/25 hover:bg-red-500 rounded-lg text-white transition-colors"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>

                        <div className="flex items-center justify-between">
                          <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                openLightbox(index);
                              }}
                              className="p-1.5 bg-white/10 hover:bg-white/25 rounded-lg text-white transition-colors"
                              title="Full Screen View"
                            >
                              <Maximize2 className="w-3.5 h-3.5" />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDownload(item);
                              }}
                              className="p-1.5 bg-white/10 hover:bg-white/25 rounded-lg text-white transition-colors"
                              title="Download"
                            >
                              <Download className="w-3.5 h-3.5" />
                            </button>
                            {canManage && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openTagModal(item);
                                }}
                                className="p-1.5 bg-white/10 hover:bg-white/25 rounded-lg text-white transition-colors"
                                title="Tag Students"
                              >
                                <Tag className="w-3.5 h-3.5" />
                              </button>
                            )}
                            <div className="relative">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setShowShareIndex(showShareIndex === index ? null : index);
                                }}
                                className="p-1.5 bg-white/10 hover:bg-white/25 rounded-lg text-white transition-colors"
                                title="Share"
                              >
                                <Share2 className="w-3.5 h-3.5" />
                              </button>
                              {showShareIndex === index && (
                                <div className="absolute bottom-8 left-0 bg-slate-900 border border-slate-800 rounded-xl p-2.5 shadow-xl flex gap-1.5 z-10">
                                  <button
                                    onClick={() => copyShareLink(item.web_view_link, index)}
                                    className="p-1.5 bg-white/5 hover:bg-white/15 text-white rounded-lg"
                                    title="Copy Link"
                                  >
                                    {copiedIndex === index ? (
                                      <CheckCircle className="w-4 h-4 text-green-400" />
                                    ) : (
                                      <Copy className="w-4 h-4" />
                                    )}
                                  </button>
                                  <button
                                    onClick={() => shareSocial("whatsapp", item.web_view_link, selectedEvent.title)}
                                    className="p-1.5 bg-green-600/30 hover:bg-green-600 text-green-400 rounded-lg"
                                    title="Share on WhatsApp"
                                  >
                                    <span className="text-xs font-bold px-1">WA</span>
                                  </button>
                                  <button
                                    onClick={() => shareSocial("facebook", item.web_view_link, selectedEvent.title)}
                                    className="p-1.5 bg-blue-600/30 hover:bg-blue-600 text-blue-400 rounded-lg"
                                    title="Share on Facebook"
                                  >
                                    <Facebook className="w-4 h-4" />
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Right Column: Uploader Sidebar */}
        {canManage && (
          <div className="space-y-6">
            {/* Statistics */}
            {selectedEvent && (
              <div className="bg-gradient-to-br from-slate-800 to-slate-900 text-white p-6 rounded-2xl shadow-md space-y-4">
                <h3 className="font-semibold text-sky-400 flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Album Statistics
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-white/5 p-3.5 rounded-xl border border-white/5">
                    <p className="text-xs text-slate-400">Total Items</p>
                    <p className="text-2xl font-bold mt-1">{media.length}</p>
                  </div>
                  <div className="bg-white/5 p-3.5 rounded-xl border border-white/5">
                    <p className="text-xs text-slate-400">Disk Saved</p>
                    <p className="text-xl font-bold mt-1 text-green-400">
                      {media.length > 0
                        ? `${Math.round(
                            (1 -
                              media.reduce((acc, m) => acc + (m.compressed_size_bytes || m.size_bytes), 0) /
                                media.reduce((acc, m) => acc + m.size_bytes, 0)) *
                              100
                          )}%`
                        : "0%"}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Drag & Drop Upload Zone */}
            <div 
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm space-y-4"
            >
              <h3 className="font-bold text-slate-800 text-base">
                {selectedEvent ? `Add Media to ${selectedEvent.title}` : "Upload to General Gallery"}
              </h3>
              
              {/* Dual upload buttons (Photos and Videos) */}
              <div className="grid grid-cols-2 gap-4">
                <div
                  onClick={() => photosInputRef.current?.click()}
                  className="border-2 border-dashed border-slate-200 hover:border-sky-400 hover:bg-sky-50/20 transition-all rounded-xl p-4 text-center cursor-pointer group flex flex-col items-center justify-center min-h-[120px]"
                >
                  <input
                    type="file"
                    multiple
                    accept="image/webp,image/jpeg,image/jpg"
                    ref={photosInputRef}
                    onChange={handleFileChange}
                    className="hidden"
                  />
                  <UploadCloud className="w-8 h-8 text-sky-400 group-hover:scale-110 transition-transform mb-2" />
                  <span className="text-xs font-bold text-slate-700">Upload Photos</span>
                  <span className="text-[10px] text-slate-400 mt-1">.webp, .jpeg (Max 10MB)</span>
                </div>

                <div
                  onClick={() => videosInputRef.current?.click()}
                  className="border-2 border-dashed border-slate-200 hover:border-indigo-400 hover:bg-indigo-50/20 transition-all rounded-xl p-4 text-center cursor-pointer group flex flex-col items-center justify-center min-h-[120px]"
                >
                  <input
                    type="file"
                    multiple
                    accept="video/mp4"
                    ref={videosInputRef}
                    onChange={handleFileChange}
                    className="hidden"
                  />
                  <Video className="w-8 h-8 text-indigo-400 group-hover:scale-110 transition-transform mb-2" />
                  <span className="text-xs font-bold text-slate-700">Upload Videos</span>
                  <span className="text-[10px] text-slate-400 mt-1">.mp4 (Max 20MB)</span>
                </div>
              </div>
              <p className="text-[10px] text-center text-slate-400">
                You can drag & drop files here too. Max 10 files per upload.
              </p>

              {/* Auto Compress Toggle */}
              <div className="flex items-center justify-between bg-slate-50 p-3 rounded-xl">
                <div className="flex flex-col">
                  <span className="text-xs font-semibold text-slate-700">Auto Storage Compressor</span>
                  <span className="text-[10px] text-slate-400">Reduces files sizes to save storage</span>
                </div>
                <input
                  type="checkbox"
                  checked={autoCompress}
                  onChange={(e) => setAutoCompress(e.target.checked)}
                  className="w-4 h-4 rounded text-sky-500 border-slate-300 focus:ring-sky-500"
                />
              </div>

              {/* Tag Students on Upload Option */}
              <div className="flex items-center justify-between bg-slate-50 p-3 rounded-xl">
                <div className="flex flex-col">
                  <span className="text-xs font-semibold text-slate-700">Upload for Individual Students</span>
                  <span className="text-[10px] text-slate-400">
                    {isForcedIndividual ? "Required for wardens (uploads for assigned students only)" : "Pre-tag selected students for these photos"}
                  </span>
                </div>
                <input
                  type="checkbox"
                  checked={isForcedIndividual ? true : tagOnUpload}
                  disabled={isForcedIndividual}
                  onChange={(e) => {
                    setTagOnUpload(e.target.checked);
                    if (!e.target.checked) {
                      setUploadSelectedStudents([]);
                      setUploadSelectedClass("All");
                      setUploadSelectedRoom("All");
                      setUploadSearchQuery("");
                    }
                  }}
                  className="w-4 h-4 rounded text-sky-500 border-slate-300 focus:ring-sky-500 disabled:opacity-50"
                />
              </div>

              {(tagOnUpload || isForcedIndividual) && (
                <div className="space-y-3 p-3 bg-slate-50 rounded-xl border border-slate-100 animate-in fade-in duration-200">
                  {/* Filters Header */}
                  <div className="border-b border-slate-200 pb-2 mb-1">
                    <span className="text-[10px] font-bold text-slate-600 uppercase flex items-center gap-1">
                      <Filter className="w-3 h-3 text-sky-500" />
                      Filter Students
                    </span>
                  </div>

                  {/* Class Filter */}
                  <div className="space-y-1">
                    <label className="text-[10px] font-bold text-slate-500 uppercase">Select Class</label>
                    <select
                      value={uploadSelectedClass}
                      onChange={(e) => {
                        setUploadSelectedClass(e.target.value);
                        setUploadSelectedStudents([]);
                      }}
                      className="w-full text-xs border-slate-200 focus:ring-sky-500 rounded-lg p-2 bg-white"
                    >
                      <option value="All">All Classes</option>
                      {uniqueClasses.map((c) => (
                        <option key={c} value={c}>Class {c}</option>
                      ))}
                    </select>
                  </div>

                  {/* Room Filter */}
                  <div className="space-y-1">
                    <label className="text-[10px] font-bold text-slate-500 uppercase">Select Room</label>
                    <select
                      value={uploadSelectedRoom}
                      onChange={(e) => {
                        setUploadSelectedRoom(e.target.value);
                        setUploadSelectedStudents([]);
                      }}
                      className="w-full text-xs border-slate-200 focus:ring-sky-500 rounded-lg p-2 bg-white"
                    >
                      <option value="All">All Rooms</option>
                      {uniqueRooms.map((r) => (
                        <option key={r} value={r}>Room {r}</option>
                      ))}
                    </select>
                  </div>

                  {/* Search Child Name Filter */}
                  <div className="space-y-1">
                    <label className="text-[10px] font-bold text-slate-500 uppercase">Search Student Name</label>
                    <div className="relative">
                      <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2 top-2.5" />
                      <input
                        type="text"
                        placeholder="Search child..."
                        value={uploadSearchQuery}
                        onChange={(e) => {
                          setUploadSearchQuery(e.target.value);
                          setUploadSelectedStudents([]);
                        }}
                        className="w-full text-xs pl-7 pr-2 py-2 border-slate-200 focus:ring-sky-500 rounded-lg bg-white"
                      />
                    </div>
                  </div>

                  {/* Checklist */}
                  <div className="space-y-1 pt-2 border-t border-slate-100">
                    <label className="text-[10px] font-bold text-slate-500 uppercase">Select Students ({uploadSelectedStudents.length})</label>
                    <div className="max-h-36 overflow-y-auto border border-slate-200 rounded-lg p-2 bg-white space-y-1">
                      {students
                        .filter((s) => {
                          const matchesClass = uploadSelectedClass === "All" || s.class_name === uploadSelectedClass;
                          const matchesRoom = uploadSelectedRoom === "All" || s.room_number === uploadSelectedRoom;
                          const matchesName = !uploadSearchQuery || s.name.toLowerCase().includes(uploadSearchQuery.toLowerCase());
                          return matchesClass && matchesRoom && matchesName;
                        })
                        .map((s) => {
                          const isChecked = uploadSelectedStudents.includes(s.id);
                          return (
                            <div key={s.id} className="flex items-center gap-2 text-xs">
                              <input
                                type="checkbox"
                                id={`upload-tag-${s.id}`}
                                checked={isChecked}
                                onChange={() => {
                                  setUploadSelectedStudents((prev) =>
                                    isChecked ? prev.filter((id) => id !== s.id) : [...prev, s.id]
                                  );
                                }}
                                className="w-3.5 h-3.5 rounded text-sky-500 border-slate-300 focus:ring-sky-500"
                              />
                              <label htmlFor={`upload-tag-${s.id}`} className="truncate cursor-pointer select-none">
                                {s.name} <span className="text-[9px] text-slate-400">({s.class_name} • Room {s.room_number || "N/A"})</span>
                              </label>
                            </div>
                          );
                        })}
                    </div>
                  </div>
                </div>
              )}

              {/* Files Upload Queue */}
              {selectedFiles.length > 0 && (
                <div className="space-y-3 pt-2">
                  <div className="flex justify-between items-center text-xs font-semibold text-slate-600">
                    <span>Queue ({selectedFiles.length} files)</span>
                    <button
                      onClick={() => setSelectedFiles([])}
                      className="text-red-500 hover:text-red-600"
                    >
                      Clear All
                    </button>
                  </div>
                  
                  <div className="max-h-48 overflow-y-auto space-y-2 border border-slate-50 rounded-lg p-2 bg-slate-50/50">
                    {selectedFiles.map((file, idx) => (
                      <div
                        key={idx}
                        className="flex items-center justify-between bg-white px-3 py-2 rounded-lg border border-slate-100 text-xs"
                      >
                        <span className="truncate max-w-[150px] font-medium text-slate-700">{file.name}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-slate-400">{(file.size / 1024 / 1024).toFixed(2)} MB</span>
                          <button
                            onClick={() => removeFileFromQueue(idx)}
                            className="text-red-400 hover:text-red-500"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>

                  {uploading ? (
                    <div className="space-y-2">
                      <Progress value={uploadProgress} className="h-1.5 bg-slate-100" />
                      <p className="text-[10px] text-slate-500 text-center animate-pulse">
                        Uploading files... Please do not close this page. ({uploadProgress}%)
                      </p>
                    </div>
                  ) : (
                    <Button
                      onClick={handleUploadFiles}
                      className="w-full bg-sky-500 hover:bg-sky-600 text-white text-sm"
                    >
                      Upload Selected Files
                    </Button>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Lightbox Overlay */}
      {lightboxOpen && media.length > 0 && (
        <div className="fixed inset-0 bg-black/95 z-[9999] flex flex-col justify-between p-4">
          <div className="flex justify-between items-center text-white">
            <span className="text-sm font-semibold">
              {lightboxIndex + 1} / {media.length} — {media[lightboxIndex].original_name}
            </span>
            <div className="flex gap-4">
              <button
                onClick={() => handleDownload(media[lightboxIndex])}
                className="text-slate-400 hover:text-white p-2"
                title="Download Original"
              >
                <Download className="w-5 h-5" />
              </button>
              <button
                onClick={() => setLightboxOpen(false)}
                className="text-slate-400 hover:text-white p-2"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>

          <div className="flex-1 flex items-center justify-center relative my-4">
            <button
              onClick={prevLightboxItem}
              className="absolute left-2 text-white/50 hover:text-white p-3 bg-white/5 hover:bg-white/10 rounded-full transition-colors"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>

            {media[lightboxIndex].media_type === "video" ? (
              <video
                src={media[lightboxIndex].web_content_link}
                controls
                autoPlay
                className="max-h-[80vh] max-w-[85vw] object-contain rounded"
              />
            ) : (
              <img
                src={media[lightboxIndex].web_view_link.replace("/view", "/uc?export=view")} // Force view url structure for clean rendering if needed, otherwise fallback to standard source
                onError={(e) => {
                  e.target.src = media[lightboxIndex].web_content_link; // direct stream fallback
                }}
                alt={media[lightboxIndex].original_name}
                className="max-h-[80vh] max-w-[85vw] object-contain rounded"
              />
            )}

            <button
              onClick={nextLightboxItem}
              className="absolute right-2 text-white/50 hover:text-white p-3 bg-white/5 hover:bg-white/10 rounded-full transition-colors"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          </div>

          <div className="text-center text-slate-500 text-xs">
            Hostel Gallery Viewer
          </div>
        </div>
      )}

      {/* CREATE EVENT MODAL */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[1000] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl border border-slate-100 space-y-4 animate-in fade-in zoom-in-95">
            <div className="flex justify-between items-center border-b border-slate-100 pb-3">
              <h3 className="font-bold text-slate-800 text-lg">Create New Album</h3>
              <button
                onClick={() => setShowCreateModal(false)}
                className="text-slate-400 hover:text-slate-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <form onSubmit={handleCreateEvent} className="space-y-4">
              <div className="space-y-1">
                <label className="text-xs font-semibold text-slate-600">Album Title</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Annual Sports Meet 2026"
                  value={newEvent.title}
                  onChange={(e) => setNewEvent({ ...newEvent, title: e.target.value })}
                  className="w-full text-sm border-slate-200 focus:border-sky-500 focus:ring-sky-500 rounded-lg p-2.5 bg-slate-50"
                />
              </div>

              <div className="space-y-1">
                <label className="text-xs font-semibold text-slate-600">Description</label>
                <textarea
                  placeholder="Tell parents what this album features..."
                  value={newEvent.description}
                  onChange={(e) => setNewEvent({ ...newEvent, description: e.target.value })}
                  className="w-full text-sm border-slate-200 focus:border-sky-500 focus:ring-sky-500 rounded-lg p-2.5 bg-slate-50 h-20"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="text-xs font-semibold text-slate-600">Event Date</label>
                  <input
                    type="date"
                    required
                    value={newEvent.date}
                    onChange={(e) => setNewEvent({ ...newEvent, date: e.target.value })}
                    className="w-full text-sm border-slate-200 focus:border-sky-500 focus:ring-sky-500 rounded-lg p-2.5 bg-slate-50"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-semibold text-slate-600">Tags (Comma-separated)</label>
                  <input
                    type="text"
                    placeholder="Class 6, Sports, Outdoor"
                    value={newEvent.tags}
                    onChange={(e) => setNewEvent({ ...newEvent, tags: e.target.value })}
                    className="w-full text-sm border-slate-200 focus:border-sky-500 focus:ring-sky-500 rounded-lg p-2.5 bg-slate-50"
                  />
                </div>
              </div>

              <div className="flex gap-2 justify-end pt-4 border-t border-slate-100">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowCreateModal(false)}
                  className="text-xs"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="bg-sky-500 hover:bg-sky-600 text-white text-xs"
                >
                  Create Album
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* EDIT EVENT MODAL */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[1000] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl border border-slate-100 space-y-4 animate-in fade-in zoom-in-95">
            <div className="flex justify-between items-center border-b border-slate-100 pb-3">
              <h3 className="font-bold text-slate-800 text-lg">Edit Album Details</h3>
              <button
                onClick={() => setShowEditModal(false)}
                className="text-slate-400 hover:text-slate-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <form onSubmit={handleUpdateEvent} className="space-y-4">
              <div className="space-y-1">
                <label className="text-xs font-semibold text-slate-600">Album Title</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Annual Sports Meet 2026"
                  value={editEventData.title}
                  onChange={(e) => setEditEventData({ ...editEventData, title: e.target.value })}
                  className="w-full text-sm border-slate-200 focus:border-sky-500 focus:ring-sky-500 rounded-lg p-2.5 bg-slate-50"
                />
              </div>

              <div className="space-y-1">
                <label className="text-xs font-semibold text-slate-600">Description</label>
                <textarea
                  placeholder="Tell parents what this album features..."
                  value={editEventData.description}
                  onChange={(e) => setEditEventData({ ...editEventData, description: e.target.value })}
                  className="w-full text-sm border-slate-200 focus:border-sky-500 focus:ring-sky-500 rounded-lg p-2.5 bg-slate-50 h-20"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="text-xs font-semibold text-slate-600">Event Date</label>
                  <input
                    type="date"
                    required
                    value={editEventData.date}
                    onChange={(e) => setEditEventData({ ...editEventData, date: e.target.value })}
                    className="w-full text-sm border-slate-200 focus:border-sky-500 focus:ring-sky-500 rounded-lg p-2.5 bg-slate-50"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-semibold text-slate-600">Tags (Comma-separated)</label>
                  <input
                    type="text"
                    placeholder="Class 6, Sports, Outdoor"
                    value={editEventData.tags}
                    onChange={(e) => setEditEventData({ ...editEventData, tags: e.target.value })}
                    className="w-full text-sm border-slate-200 focus:border-sky-500 focus:ring-sky-500 rounded-lg p-2.5 bg-slate-50"
                  />
                </div>
              </div>

              <div className="flex gap-2 justify-end pt-4 border-t border-slate-100">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowEditModal(false)}
                  className="text-xs"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="bg-sky-500 hover:bg-sky-600 text-white text-xs"
                >
                  Save Changes
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* STUDENT TAGGING MODAL */}
      {showTagModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[1000] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl border border-slate-100 flex flex-col max-h-[85vh] animate-in fade-in zoom-in-95">
            <div className="flex justify-between items-center border-b border-slate-100 pb-3 mb-4">
              <div>
                <h3 className="font-bold text-slate-800 text-base">Tag Students in Media</h3>
                <p className="text-slate-400 text-xs mt-0.5">Tagged children's parents will see this photo prominently.</p>
              </div>
              <button
                onClick={() => setShowTagModal(false)}
                className="text-slate-400 hover:text-slate-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Search and Filters */}
            <div className="space-y-3 mb-4">
              <div className="relative">
                <Search className="w-4 h-4 text-slate-400 absolute left-3 top-3" />
                <input
                  type="text"
                  placeholder="Search students by name, class or roll..."
                  value={searchStudentQuery}
                  onChange={(e) => setSearchStudentQuery(e.target.value)}
                  className="w-full text-xs pl-9 pr-4 py-2.5 rounded-lg border-slate-200 focus:border-sky-500 focus:ring-sky-500 bg-slate-50"
                />
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[9px] font-bold text-slate-400 uppercase">Class Filter</label>
                  <select
                    value={tagModalSelectedClass}
                    onChange={(e) => setTagModalSelectedClass(e.target.value)}
                    className="w-full text-[11px] border-slate-200 focus:ring-sky-500 rounded-lg p-1.5 bg-slate-50"
                  >
                    <option value="All">All Classes</option>
                    {uniqueClasses.map((c) => (
                      <option key={c} value={c}>Class {c}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-[9px] font-bold text-slate-400 uppercase">Room Filter</label>
                  <select
                    value={tagModalSelectedRoom}
                    onChange={(e) => setTagModalSelectedRoom(e.target.value)}
                    className="w-full text-[11px] border-slate-200 focus:ring-sky-500 rounded-lg p-1.5 bg-slate-50"
                  >
                    <option value="All">All Rooms</option>
                    {uniqueRooms.map((r) => (
                      <option key={r} value={r}>Room {r}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Student List */}
            <div className="flex-1 overflow-y-auto min-h-[300px] border border-slate-50 rounded-lg p-2 space-y-1.5">
              {filteredStudents.length === 0 ? (
                <div className="text-center py-12 text-slate-400 text-xs">
                  No students match your search query.
                </div>
              ) : (
                filteredStudents.map((s) => {
                  const isTagged = taggedStudentsTemp.includes(s.id);
                  return (
                    <div
                      key={s.id}
                      onClick={() => toggleStudentTag(s.id)}
                      className={`flex items-center justify-between px-3.5 py-2.5 rounded-xl border text-xs cursor-pointer transition-colors ${
                        isTagged
                          ? "bg-sky-50 border-sky-200 text-sky-700"
                          : "bg-white border-slate-100 text-slate-700 hover:bg-slate-50"
                      }`}
                    >
                      <div className="flex flex-col">
                        <span className="font-semibold">{s.name}</span>
                        <span className="text-[10px] text-slate-400 mt-0.5">
                          Class: {s.class_name || "N/A"} | Room: {s.room_number || "Not assigned"}
                        </span>
                      </div>
                      <div
                        className={`w-5 h-5 rounded-full flex items-center justify-center border ${
                          isTagged
                            ? "bg-sky-500 border-sky-500 text-white"
                            : "border-slate-200 bg-white"
                        }`}
                      >
                        {isTagged && <Check className="w-3.5 h-3.5 stroke-[3]" />}
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            <div className="flex gap-2 justify-end pt-4 border-t border-slate-100 mt-4">
              <Button
                variant="outline"
                onClick={() => setShowTagModal(false)}
                className="text-xs"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveTags}
                className="bg-sky-500 hover:bg-sky-600 text-white text-xs"
              >
                Save Tags ({taggedStudentsTemp.length})
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GalleryManager;
